import {
    usePropertyContactIsPrimary,
    usePropertyContactFirstName,
    usePropertyContactMiddleName,
    usePropertyContactLastName,
    usePropertyContactPhone,
    usePropertyContactEmail,
} from '../../../queries/PropertyFields/NonRentalContactFields';
import { useBorrowerLoanType } from '../../../queries/BorrowerFields/CoreFields';
import {
    useBorrowerPrincipalFirstName,
    useBorrowerPrincipalMiddleName,
    useBorrowerPrincipalLastName,
    useBorrowerPrincipalPhone,
    useBorrowerPrincipalEmail,
} from '../../../queries/BorrowerFields/PrincipalFields';
import { ToggleButtonLabelSize, YesNoToggleButton } from '@apply/components';
import { ButtonGroupTypes } from 'devextreme-react/button-group';
import { LoanType } from '@apply/constants';
import { ApplicationQuestionReviewItem } from '../../components/ApplicationQuestionReviewItem';

interface OnsitePropertyContactProps {
    propertyGuid: string;
    required?: boolean;
    isReview?: boolean;
}

export const OnsitePropertyContact: React.FC<OnsitePropertyContactProps> = ({
    propertyGuid,
    required = false,
    isReview = false,
}) => {
    const [loanType] = useBorrowerLoanType();

    const [contactIsPrimary, set_contactIsPrimary] = usePropertyContactIsPrimary(propertyGuid);

    const [, set_contactFirstName] = usePropertyContactFirstName(propertyGuid);
    const [, set_contactMiddleName] = usePropertyContactMiddleName(propertyGuid);
    const [, set_contactLastName] = usePropertyContactLastName(propertyGuid);
    const [, set_contactPhone] = usePropertyContactPhone(propertyGuid);
    const [, set_contactEmail] = usePropertyContactEmail(propertyGuid);

    const [principalFirstName] = useBorrowerPrincipalFirstName();
    const [principalMiddleName] = useBorrowerPrincipalMiddleName();
    const [principalLastName] = useBorrowerPrincipalLastName();
    const [principalPhone] = useBorrowerPrincipalPhone();
    const [principalEmail] = useBorrowerPrincipalEmail();

    const toggleButtonYesNoClick = (e: ButtonGroupTypes.ItemClickEvent) => {
        switch (e.itemData?.id.toLowerCase()) {
            case 'yes':
                set_contactIsPrimary(true);
                set_contactFirstName(principalFirstName);
                set_contactMiddleName(principalMiddleName);
                set_contactLastName(principalLastName);
                set_contactPhone(principalPhone);
                set_contactEmail(principalEmail);
                return;
            case 'no':
                set_contactIsPrimary(false);
                set_contactFirstName('');
                set_contactMiddleName('');
                set_contactLastName('');
                set_contactPhone('');
                set_contactEmail('');
                return;
            default:
                return;
        }
    };

    const getLabel = (showEmphasis: boolean) => {
        const prefix = loanType !== LoanType.BuildRent ? 'Will' : 'While work is being performed, will';
        return (
            <>
                {prefix} {showEmphasis ? <i>{formatFullName()}</i> : formatFullName()} be the onsite property contact?
            </>
        );
    };

    const formatFullName = () => {
        if (!principalLastName && !principalFirstName) {
            return '';
        }
        const middle = principalMiddleName ? ` ${principalMiddleName} ` : ' ';
        return `${principalFirstName}${middle}${principalLastName}`.trim();
    };

    return (
        <>
            {isReview ? (
                <ApplicationQuestionReviewItem
                    label={getLabel(false)}
                    value={contactIsPrimary ? 'Yes' : 'No'}
                />
            ) : (
                <YesNoToggleButton
                    id='toggleButtonContactIsPrimary'
                    value={contactIsPrimary}
                    onItemClick={toggleButtonYesNoClick}
                    width='230px'
                    height='40px'
                    required={required}
                    requiredMessage='Onsite Property Contact required'
                    label={getLabel(true)}
                    labelSize={ToggleButtonLabelSize.Medium}
                />
            )}
        </>
    );
};
